import { ReactNode, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { LabelWithTooltipIcon, MultilineInput } from './components';
import { MIN_CHARACTERS } from './constants';
import { convertToEscapedStringifiedJson } from './utils';

import { ForwardAlertV2Props } from './types';

const bold = (chunks: ReactNode[]) => (
  <Typography display="inline" fontWeight="medium">
    {chunks}
  </Typography>
);

const ForwardAlertV2: React.FC<ForwardAlertV2Props> = ({
  confirmedCriticality,
}) => {
  const [symptoms, setSymptoms] = useState('');
  const [reason, setReason] = useState('');
  const [situation, setSituation] = useState('');
  const [ack, setAck] = useState(false);

  const validSymptoms = symptoms.length >= MIN_CHARACTERS;
  const validReason = reason.length >= MIN_CHARACTERS;
  const validSituation = situation.length > 0;

  const canSubmit = validSymptoms && validReason && validSituation && ack;

  const handleConfirmedCriticality = () => {
    confirmedCriticality(
      convertToEscapedStringifiedJson(symptoms, reason, situation),
    );
  };

  const { formatMessage } = useIntl();

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <Stack spacing={4} justifyContent="space-between">
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight="medium">
              <FormattedMessage id="ENDUSER.DRAWER.CRITICAL.FORWARDALERT.TITLE" />
            </Typography>

            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="ENDUSER.DRAWER.CRITICAL.FORWARDALERT.SUBTITLE" />
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight="medium"
            >
              <FormattedMessage id="ENDUSER.DRAWER.CRITICAL.FORWARDALERT.SUBTITLE.OBS" />
            </Typography>
          </Stack>

          <Stack spacing={2}>
            <LabelWithTooltipIcon
              labelKey="CPPLATFORM.CRITICALPATIENT.Q1TITLE"
              tooltipKey="CPPLATFORM.CRITICALPATIENT.Q1TOOLTIP"
            />
            <FormControl>
              {/* This FormControl is required to use `focused` from `useFormControl` */}
              <MultilineInput
                value={symptoms}
                onChange={setSymptoms}
                placeholderKey="CPPLATFORM.CRITICALPATIENT.Q1SYMPTOMS"
                error={!validSymptoms}
              />
            </FormControl>
          </Stack>

          <Stack spacing={2}>
            <LabelWithTooltipIcon
              labelKey="CPPLATFORM.CRITICALPATIENT.Q2TITLE"
              tooltipKey="CPPLATFORM.CRITICALPATIENT.Q2TOOLTIP"
            />
            <FormControl>
              <MultilineInput
                value={reason}
                onChange={setReason}
                placeholderKey="CPPLATFORM.CRITICALPATIENT.Q2REASON"
                error={!validReason}
              />
            </FormControl>
          </Stack>

          <Stack spacing={2}>
            <LabelWithTooltipIcon
              labelKey="CPPLATFORM.CRITICALPATIENT.Q3TITLE"
              tooltipKey="CPPLATFORM.CRITICALPATIENT.Q3TOOLTIP"
            />
            <FormControl>
              <RadioGroup
                value={situation}
                onChange={e => setSituation(e.target.value)}
                sx={{ ml: 1.5 }}
              >
                <FormControlLabel
                  value="imminent_risk"
                  control={<Radio />}
                  label={formatMessage(
                    { id: 'CPPLATFORM.CRITICALPATIENT.Q3OPTION1' },
                    { b: bold },
                  )}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4, mb: 1 }}
                >
                  <FormattedMessage id="CPPLATFORM.CRITICALPATIENT.Q3OPTION1SUBTITLE" />
                </Typography>

                <FormControlLabel
                  value="possibility_aggravate_symptoms"
                  control={<Radio />}
                  label={formatMessage(
                    { id: 'CPPLATFORM.CRITICALPATIENT.Q3OPTION2' },
                    { b: bold },
                  )}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4 }}
                >
                  <FormattedMessage id="CPPLATFORM.CRITICALPATIENT.Q3OPTION2SUBTITLE" />
                </Typography>
              </RadioGroup>
            </FormControl>
          </Stack>
        </Stack>

        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Checkbox value={ack} onChange={e => setAck(e.target.checked)} />
            }
            label={formatMessage(
              { id: 'CPPLATFORM.CRITICALPATIENT.CHECKBOX' },
              { b: bold },
            )}
          />
          <Button
            variant="contained"
            onClick={handleConfirmedCriticality}
            sx={{ width: 'fit-content' }}
            size="large"
            disabled={!canSubmit}
          >
            <FormattedMessage id="CPPLATFORM.CRITICALPATIENTBUTTON.SEND" />
          </Button>
          {/** All attempts of adding a margin bottom failed, that's why this empty div is here ;) */}
          <div />
        </Stack>
      </Stack>
    </Box>
  );
};

export default ForwardAlertV2;
